import React, { useEffect, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Pin, PinNote } from '@/design-system'
import { StudioPin } from '../../types'
import { UseOverlayProjectionApi } from '../../hooks/useOverlayProjection'

export type PinRevealMode = 'hover' | 'click'

export interface StudioOverlaysProps {
  projection: UseOverlayProjectionApi
  selectedArt: { id: string; name: string; w: number; ratio: number; wallLabel: string } | null
  getSelectedTagPos: () => BABYLON.Vector3 | null
  pins: StudioPin[]
  getPinPos: (pin: StudioPin) => BABYLON.Vector3 | null
  showPins: boolean
  /** Showcase-only "Priya · demo" cursor — off until realtime presence exists. */
  showPresenceDemo: boolean
  presencePos: () => BABYLON.Vector3 | null
  /** Host: open compose/edit. Guest / Present: omit for read-only expand. */
  onPinClick?: (pin: StudioPin) => void
  /**
   * Fires when a guest/Present viewer *opens* a pin's note (`reveal='click'`
   * only) — not on close. Guests can't author pins themselves (the scene is
   * one shared JSON note per gallery, no per-pin write path); this is the
   * hook for turning that read-only moment into a signup nudge instead.
   */
  onPinReveal?: (pinId: string) => void
  /** Highlight the pin currently being composed/edited (or Print Inspector peek). */
  activePinId?: string | null
  /**
   * How note cards open.
   * - `hover` (default): studio edit — hover expands, click opens compose.
   * - `click`: Present / guest — marker only until click/tap; soft pulse while collapsed.
   */
  reveal?: PinRevealMode
}

const OverlayBase = styled(Box)({ position: 'absolute', zIndex: 20, transition: 'opacity 200ms ease' })

const SelTag = styled(OverlayBase)({
  transform: 'translate(-50%, -130%)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 10px',
  borderRadius: 8,
  background: 'rgba(19,18,25,0.9)',
  border: '1px solid rgba(190,157,253,0.5)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
})
const SelName = styled('span')({ fontSize: 11.5, fontWeight: 650, color: 'var(--ds-night-ink)' })
const SelDims = styled('span')({ fontFamily: 'var(--ds-font-mono)', fontSize: 8, letterSpacing: '0.06em', color: '#be9dfd' })

function SelectionTag({ selectedArt, getPos, register, unregister }: {
  selectedArt: StudioOverlaysProps['selectedArt']
  getPos: () => BABYLON.Vector3 | null
  register: UseOverlayProjectionApi['register']
  unregister: UseOverlayProjectionApi['unregister']
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current || !selectedArt) return
    register('sel-tag', ref.current, { getWorldPos: getPos })
    return () => unregister('sel-tag')
  }, [selectedArt, getPos, register, unregister])

  if (!selectedArt) return null
  const height = selectedArt.w / selectedArt.ratio
  return (
    <SelTag ref={ref} style={{ opacity: 0 }}>
      <SelName>{selectedArt.name}</SelName>
      <SelDims>
        {selectedArt.w.toFixed(1)} × {height.toFixed(1)} M · {selectedArt.wallLabel}
      </SelDims>
    </SelTag>
  )
}

// Pin renders at a fixed 26x26 (design system default) — center it on its anchor point.
const PinDot = styled(OverlayBase)({ margin: '-13px 0 0 -13px', cursor: 'pointer' })

function PinMarker({ pin, getPos, register, unregister, onPinClick, active, expanded, onHover, onToggle, readOnly, reveal }: {
  pin: StudioPin
  getPos: () => BABYLON.Vector3 | null
  register: UseOverlayProjectionApi['register']
  unregister: UseOverlayProjectionApi['unregister']
  onPinClick?: (pin: StudioPin) => void
  active?: boolean
  /** Only one sticky card open at a time among stacked pins. */
  expanded?: boolean
  onHover?: (pinId: string | null) => void
  onToggle?: (pinId: string) => void
  readOnly?: boolean
  reveal: PinRevealMode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current) return
    register(`pin-${pin.id}`, ref.current, { getWorldPos: getPos })
    return () => unregister(`pin-${pin.id}`)
  }, [pin.id, getPos, register, unregister])

  const showNote = expanded || active
  const hasText = pin.text.trim().length > 0
  const pulse = reveal === 'click' && !showNote

  return (
    <PinDot
      ref={ref}
      style={{ opacity: 0 }}
      onMouseEnter={() => {
        if (reveal === 'hover') onHover?.(pin.id)
      }}
      onMouseLeave={() => {
        if (reveal === 'hover' && !active) onHover?.(null)
      }}
      onClick={(e) => {
        e.stopPropagation()
        if (reveal === 'click') {
          onToggle?.(pin.id)
          return
        }
        if (onPinClick) onPinClick(pin)
        else onHover?.(expanded ? null : pin.id)
      }}
    >
      <Pin
        tone={pin.tone}
        label={pin.authorInitial}
        pulse={pulse}
        anchor={{ left: '0', top: '0' }}
        sx={{ position: 'static' }}
      />
      {showNote && hasText && (
        <PinNote
          avatarLabel={pin.authorInitial}
          name={readOnly ? pin.authorName : `${pin.authorName}${onPinClick ? ' · edit' : ''}`}
          note={pin.text}
          anchor={{ left: '16px', top: '-6px' }}
          sx={{ position: 'absolute', pointerEvents: 'none', zIndex: 2 }}
        />
      )}
    </PinDot>
  )
}

// 1:1 with the mockup's `.mp-cursor` — a pointer-arrow SVG (like a live
// cursor in Figma/Docs), not a circular avatar bubble. The "· demo" suffix
// on the flag is a deliberate deviation from the mockup's plain "Priya" —
// this cursor isn't backed by realtime multiplayer, so it discloses that
// (see StudioOverlays' file-level comment below / the phase-1 scope decision).
const PresenceRoot = styled(OverlayBase)({ zIndex: 25, pointerEvents: 'none', display: 'flex', alignItems: 'flex-start' })
const CursorArrow = styled('svg')({ display: 'block', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.5))', flexShrink: 0 })
const PresenceFlag = styled(Box)({
  margin: '1px 0 0 12px',
  padding: '2px 8px',
  borderRadius: '4px 10px 10px 10px',
  background: '#01dafe',
  color: '#063a44',
  fontSize: 9.5,
  fontWeight: 750,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
})

function PresenceCursor({ getPos, register, unregister }: {
  getPos: () => BABYLON.Vector3 | null
  register: UseOverlayProjectionApi['register']
  unregister: UseOverlayProjectionApi['unregister']
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current) return
    register('presence-cursor', ref.current, { getWorldPos: getPos, always: true })
    return () => unregister('presence-cursor')
  }, [getPos, register, unregister])

  return (
    <PresenceRoot ref={ref} style={{ opacity: 0 }}>
      <CursorArrow width={14} height={16} viewBox='0 0 14 16'>
        <path d='M1 1 L13 8 L7.2 9.4 L4.6 15 Z' fill='#01dafe' stroke='#063a44' strokeWidth={1} />
      </CursorArrow>
      <PresenceFlag>Priya · demo</PresenceFlag>
    </PresenceRoot>
  )
}

/**
 * Canvas-space overlays projected from 3D world points — selection tag,
 * story pins, and a presence cursor. The cursor is an explicitly-labelled
 * visual placeholder ("· demo") — there's no realtime multiplayer
 * infrastructure behind it yet, per the phase-1 scope decision.
 */
export function StudioOverlays({
  projection,
  selectedArt,
  getSelectedTagPos,
  pins,
  getPinPos,
  showPins,
  showPresenceDemo,
  presencePos,
  onPinClick,
  onPinReveal,
  activePinId,
  reveal = 'hover',
}: StudioOverlaysProps) {
  const readOnly = !onPinClick
  const [hoveredPinId, setHoveredPinId] = useState<string | null>(null)
  const [toggledPinId, setToggledPinId] = useState<string | null>(null)

  // Forced peek / compose (`activePinId`) always wins; otherwise reveal mode
  // picks hover vs click-toggle as the single expanded card.
  const expandedId =
    activePinId || (reveal === 'click' ? toggledPinId : hoveredPinId)

  const onToggle = (pinId: string) => {
    setToggledPinId((cur) => {
      const next = cur === pinId ? null : pinId
      if (next) onPinReveal?.(next)
      return next
    })
  }

  return (
    <>
      <SelectionTag selectedArt={selectedArt} getPos={getSelectedTagPos} register={projection.register} unregister={projection.unregister} />
      {showPins &&
        pins.map((pin) => (
          <PinMarker
            key={pin.id}
            pin={pin}
            getPos={() => getPinPos(pin)}
            register={projection.register}
            unregister={projection.unregister}
            onPinClick={onPinClick}
            active={activePinId === pin.id}
            expanded={expandedId === pin.id}
            onHover={setHoveredPinId}
            onToggle={onToggle}
            readOnly={readOnly}
            reveal={reveal}
          />
        ))}
      {showPresenceDemo && (
        <PresenceCursor getPos={presencePos} register={projection.register} unregister={projection.unregister} />
      )}
    </>
  )
}
